import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, FileSearch, MessageSquare, CheckCircle, ArrowRight, Zap, Award, Building2, Factory, Mail, Phone, MapPin, Target, Lightbulb, Heart, Route, ChevronDown, Menu, X, Briefcase, Bot, HelpCircle, AlertCircle, FileText, TrendingUp, Calendar, Users, BarChart3, ExternalLink, GraduationCap } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// FireSight Pro tool — external landing page (update this URL when the new tool page goes live)
const FIRESIGHT_PRO_URL = "https://www.kavachfiresight.ai";

// Leadership section hidden per user request — flip to true to re-enable
const SHOW_TEAM_SECTION = false;

// Fire Engineering Pro URL - Custom Domain (Live)
const FIRE_ENGINEERING_PRO_URL = "https://engineering.kavachinfra.in";

const HEADER_LOGO_URL = "/kavach_logo_hd.png";
const CONTENT_LOGO_URL = "/kavach_logo_hd.png";
const KAVACH_3D_LOGO_URL = "/kavach_3d_logo.jpg";
const KAVACH_2D_LOGO_URL = "/kavach_logo_hd.png";

export default function LandingPage() {
  const [productsOpen, setProductsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Analytics tracking function
  const trackProductClick = useCallback((product, action, source = 'landing_page') => {
    // Fire-and-forget analytics tracking
    fetch(`${API_URL}/api/analytics/product-click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        product,
        action,
        source,
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        referrer: document.referrer || null
      })
    }).catch(() => {}); // Silently ignore errors
  }, []);

  // Tracked click handlers — FireSight Pro now lives on its own site
  const handleFireSightClick = (source) => {
    trackProductClick('firesight_pro', 'click', source);
    window.open(FIRESIGHT_PRO_URL, '_blank');
  };

  const handleEngineeringProClick = (source) => {
    trackProductClick('engineering_pro', 'click', source);
    // Redirect to Fire Engineering Pro production deployment
    window.open(FIRE_ENGINEERING_PRO_URL, '_blank');
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div data-testid="landing-page" className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white border-b border-slate-200 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex justify-between items-center gap-6">
            {/* Company Logo and Name */}
            <div className="flex items-center gap-2 flex-shrink min-w-0">
              <img src={HEADER_LOGO_URL} alt="Kavach Logo" className="h-10 w-10 object-contain flex-shrink-0" />
              <div className="flex flex-col min-w-0">
                <h1 className="text-base xl:text-lg font-bold whitespace-nowrap" style={{color: '#003366'}}>KAVACH INFRA SOLUTIONS LLP</h1>
                <p className="text-xs whitespace-nowrap overflow-hidden text-ellipsis" style={{color: '#D4AF37', fontWeight: 600}}>रक्षा | सुरक्षा | सजगता <span className="text-slate-500 font-normal">| Protection | Safety | Awareness</span></p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-5 flex-shrink-0">
              <button onClick={() => scrollToSection('home')} className="text-sm font-medium text-slate-700 hover:text-blue-800 transition-colors">
                Home
              </button>
              
              {/* Products Dropdown */}
              <div className="relative">
                <button 
                  onMouseEnter={() => setProductsOpen(true)}
                  onMouseLeave={() => setProductsOpen(false)}
                  className="flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-blue-800 transition-colors"
                >
                  Products <ChevronDown className="w-4 h-4" />
                </button>
                {productsOpen && (
                  <div 
                    onMouseEnter={() => setProductsOpen(true)}
                    onMouseLeave={() => setProductsOpen(false)}
                    className="absolute top-full left-0 mt-2 w-64 bg-white border border-slate-200 rounded-lg shadow-xl py-2"
                  >
                    <button onClick={() => handleFireSightClick('products_dropdown')} className="block w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-800">
                      <div className="font-semibold">Kavach FireSight Pro</div>
                      <div className="text-xs text-slate-500">AI Fire Safety Compliance Analysis</div>
                    </button>
                    <button onClick={() => handleEngineeringProClick('products_dropdown')} className="block w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-green-50 hover:text-green-800">
                      <div className="font-semibold">Fire Engineering Pro</div>
                      <div className="text-xs text-slate-500">Fire Protection Project Scheduling</div>
                    </button>
                  </div>
                )}
              </div>

              <button onClick={() => scrollToSection('about')} className="text-sm font-medium text-slate-700 hover:text-blue-800 transition-colors">
                Our Foundation
              </button>
              <button onClick={() => scrollToSection('services')} className="text-sm font-medium text-slate-700 hover:text-blue-800 transition-colors">
                Services
              </button>
              {SHOW_TEAM_SECTION && (
                <button onClick={() => scrollToSection('team')} data-testid="nav-team" className="text-sm font-medium text-slate-700 hover:text-blue-800 transition-colors">
                  Team
                </button>
              )}
              <a href="/faq" className="text-sm font-medium text-slate-700 hover:text-blue-800 transition-colors">
                FAQ
              </a>
              <a href="/pricing" data-testid="nav-pricing" className="text-sm font-medium text-slate-700 hover:text-blue-800 transition-colors">
                Pricing
              </a>
              <a href="/career" className="text-sm font-medium text-slate-700 hover:text-blue-800 transition-colors">
                Career
              </a>
              <button onClick={() => scrollToSection('contact')} className="text-sm font-medium text-slate-700 hover:text-blue-800 transition-colors">
                Contact
              </button>
              
              <Button
                data-testid="header-login-btn"
                onClick={() => handleFireSightClick('header')}
                style={{backgroundColor: '#003366'}}
                className="hover:opacity-90 text-white text-xs px-4"
              >
                Try FireSight Pro
              </Button>
              <Button
                data-testid="header-engineering-btn"
                onClick={() => handleEngineeringProClick('header')}
                style={{backgroundColor: '#166534'}}
                className="hover:opacity-90 text-white text-xs px-4"
              >
                Engineering Pro
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-slate-200 pt-4">
              <div className="flex flex-col gap-3">
                <button onClick={() => scrollToSection('home')} className="text-left text-sm font-medium text-slate-700 hover:text-blue-800">
                  Home
                </button>
                <div className="pl-2 space-y-2">
                  <div className="text-sm font-semibold text-slate-600">Products</div>
                  <button onClick={() => handleFireSightClick('mobile_menu')} className="block pl-4 text-sm text-slate-700 text-left">Kavach FireSight Pro</button>
                  <button onClick={() => handleEngineeringProClick('mobile_menu')} className="block pl-4 text-sm text-slate-700 text-left">Fire Engineering Pro</button>
                </div>
                <button onClick={() => scrollToSection('about')} className="text-left text-sm font-medium text-slate-700 hover:text-blue-800">
                  Our Foundation
                </button>
                <button onClick={() => scrollToSection('services')} className="text-left text-sm font-medium text-slate-700 hover:text-blue-800">
                  Services
                </button>
                {SHOW_TEAM_SECTION && (
                  <button onClick={() => scrollToSection('team')} className="text-left text-sm font-medium text-slate-700 hover:text-blue-800">
                    Team
                  </button>
                )}
                <a href="/faq" className="text-left text-sm font-medium text-slate-700 hover:text-blue-800">
                  FAQ
                </a>
                <a href="/pricing" className="text-left text-sm font-medium text-slate-700 hover:text-blue-800">
                  Pricing
                </a>
                <a href="/career" className="text-left text-sm font-medium text-slate-700 hover:text-blue-800">
                  Career
                </a>
                <button onClick={() => scrollToSection('contact')} className="text-left text-sm font-medium text-slate-700 hover:text-blue-800">
                  Contact
                </button>
                <Button
                  onClick={() => handleFireSightClick('mobile_login')}
                  style={{backgroundColor: '#003366'}}
                  className="hover:opacity-90 text-white w-full mb-2"
                >
                  FireSight Pro Login
                </Button>
                <Button
                  onClick={() => handleEngineeringProClick('mobile_login')}
                  style={{backgroundColor: '#166534'}}
                  className="hover:opacity-90 text-white w-full"
                >
                  Engineering Pro Login
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section with Tagline */}
      <section id="home" className="pt-28 pb-16 px-6 bg-gradient-to-br from-blue-50 via-slate-50 to-amber-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              {/* Logo and Tagline Row */}
              <div className="flex items-center gap-6 mb-6">
                <img 
                  src={KAVACH_2D_LOGO_URL} 
                  alt="Kavach Logo" 
                  className="w-32 h-32 object-contain"
                />
                <div className="p-4 rounded-xl border-2 shadow-lg flex-1" style={{borderColor: '#D4AF37', backgroundColor: '#fffef7'}}>
                  <p className="text-2xl font-bold mb-1 text-center" style={{color: '#003366'}}>
                    रक्षा | सुरक्षा | सजगता
                  </p>
                  <p className="text-base font-semibold text-center" style={{color: '#D4AF37'}}>
                    Protection | Safety | Awareness
                  </p>
                </div>
              </div>
              
              <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-2">
                Kavach Infra Solutions LLP
              </h1>
              <h2 className="text-2xl lg:text-3xl font-bold mb-6" style={{color: '#003366'}}>AI Powered Safety & Security Solution Providers</h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Kavach Infra Solutions LLP is a premier provider of electronic security systems, fire safety solutions and disaster management services. We specialise in technology-driven safety systems leveraging artificial intelligence, IoT, robotics and advanced surveillance. Our comprehensive approach covers real-time monitoring, risk assessment, regulatory advisory, training programmes and AI-led compliance auditing to ensure the highest protection standards across diverse industries and recognised code regimes such as NBC, NFPA, BS and OSHA.
              </p>
              <div className="flex flex-wrap gap-4 mb-12">
                <Button
                  data-testid="hero-get-started-btn"
                  onClick={() => handleFireSightClick('hero')}
                  size="lg"
                  style={{backgroundColor: '#003366'}}
                  className="hover:opacity-90 text-white px-8 py-6 text-lg"
                >
                  Get Started <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  onClick={() => window.location.href = '/pricing'}
                  size="lg"
                  variant="outline"
                  className="px-8 py-6 text-lg border-2"
                  style={{borderColor: '#003366', color: '#003366'}}
                  data-testid="hero-pricing-btn"
                >
                  See Pricing
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-6 pt-6 border-t border-slate-200">
                <div>
                  <p className="text-2xl font-bold" style={{color: '#D4AF37'}}>Pan-India</p>
                  <p className="text-sm text-slate-600">Active consulting</p>
                </div>
                <div>
                  <p className="text-2xl font-bold" style={{color: '#D4AF37'}}>27 codes</p>
                  <p className="text-sm text-slate-600">NBC + fire codes</p>
                </div>
                <div>
                  <p className="text-2xl font-bold" style={{color: '#D4AF37'}}>AI-Powered</p>
                  <p className="text-sm text-slate-600">Safety solutions</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl shadow-2xl p-8 border border-slate-200 bg-white">
                <div className="flex justify-center mb-4">
                  <img
                    src={KAVACH_3D_LOGO_URL}
                    alt="Kavach Logo"
                    className="w-72 h-72 object-contain"
                  />
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold mb-2" style={{color: '#003366'}}>रक्षा | सुरक्षा | सजगता</p>
                  <p className="text-sm text-slate-600">Protection | Safety | Awareness</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose FireSight Pro Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">
              Why Choose Kavach FireSight Pro?
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              AI + Computer Vision powered fire safety compliance analysis for architects and engineers
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="p-5 rounded-xl bg-blue-50 border-2 border-blue-200 hover:shadow-lg transition-all">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{backgroundColor: '#003366'}}>
                <Target className="w-5 h-5" style={{color: '#D4AF37'}} />
              </div>
              <h3 className="text-base font-bold mb-2" style={{color: '#003366'}}>Exact Symbol Detection</h3>
              <p className="text-sm text-slate-600">Computer Vision precisely counts sprinklers from PDF vectors — no estimation</p>
            </div>

            <div className="p-5 rounded-xl bg-blue-50 border-2 border-blue-200 hover:shadow-lg transition-all">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{backgroundColor: '#003366'}}>
                <Route className="w-5 h-5" style={{color: '#D4AF37'}} />
              </div>
              <h3 className="text-base font-bold mb-2" style={{color: '#003366'}}>Vector Pipe Measurement</h3>
              <p className="text-sm text-slate-600">Accurate pipe length extraction directly from CAD drawing vectors</p>
            </div>

            <div className="p-5 rounded-xl bg-blue-50 border-2 border-blue-200 hover:shadow-lg transition-all">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{backgroundColor: '#003366'}}>
                <CheckCircle className="w-5 h-5" style={{color: '#D4AF37'}} />
              </div>
              <h3 className="text-base font-bold mb-2" style={{color: '#003366'}}>Detected vs Required</h3>
              <p className="text-sm text-slate-600">Clear comparison of what's in your drawing vs NBC/NFPA requirements</p>
            </div>

            <div className="p-5 rounded-xl bg-blue-50 border-2 border-blue-200 hover:shadow-lg transition-all">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{backgroundColor: '#003366'}}>
                <Building2 className="w-5 h-5" style={{color: '#D4AF37'}} />
              </div>
              <h3 className="text-base font-bold mb-2" style={{color: '#003366'}}>Multi-Floor Analysis</h3>
              <p className="text-sm text-slate-600">Per-floor compliance with cumulative building-level reports</p>
            </div>
          </div>

          {/* New Features Highlight Row */}
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                  <Zap className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-green-800 text-sm">Background Processing</h4>
                  <p className="text-xs text-green-600">No timeouts — analysis runs async with live progress</p>
                </div>
              </div>
            </div>
            <div className="p-4 rounded-lg bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-amber-800 text-sm">Hazard Classification</h4>
                  <p className="text-xs text-amber-600">Choose Light, Ordinary, or Extra hazard for accurate BOQ</p>
                </div>
              </div>
            </div>
            <div className="p-4 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{backgroundColor: '#003366'}}>
                  <FileText className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-sm" style={{color: '#003366'}}>Professional PDF Reports</h4>
                  <p className="text-xs text-blue-600">Branded reports mirror the portal UI exactly</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Fire Engineering Pro Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-green-50 via-white to-emerald-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">
              Why Choose Fire Engineering Pro?
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Advanced project scheduling and management for fire protection contractors
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="p-5 rounded-xl bg-green-50 border-2 border-green-200 hover:shadow-lg transition-all">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{backgroundColor: '#166534'}}>
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-base font-bold mb-2 text-green-800">Smart Scheduling</h3>
              <p className="text-sm text-slate-600">AI-optimized project timelines with resource allocation</p>
            </div>

            <div className="p-5 rounded-xl bg-green-50 border-2 border-green-200 hover:shadow-lg transition-all">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{backgroundColor: '#166534'}}>
                <Users className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-base font-bold mb-2 text-green-800">Team Management</h3>
              <p className="text-sm text-slate-600">Assign crews and track progress in real-time</p>
            </div>

            <div className="p-5 rounded-xl bg-green-50 border-2 border-green-200 hover:shadow-lg transition-all">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{backgroundColor: '#166534'}}>
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-base font-bold mb-2 text-green-800">Progress Tracking</h3>
              <p className="text-sm text-slate-600">Visual dashboards with milestone and deadline alerts</p>
            </div>

            <div className="p-5 rounded-xl bg-green-50 border-2 border-green-200 hover:shadow-lg transition-all">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{backgroundColor: '#166534'}}>
                <FileText className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-base font-bold mb-2 text-green-800">Documentation</h3>
              <p className="text-sm text-slate-600">Automated reports and compliance documentation</p>
            </div>
          </div>

          <div className="text-center mt-8">
            <Button
              data-testid="try-engineering-pro-btn"
              onClick={() => handleEngineeringProClick('why_choose_section')}
              size="lg"
              style={{backgroundColor: '#166534'}}
              className="hover:opacity-90 text-white px-8"
            >
              Try Fire Engineering Pro <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Products Showcase */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Our AI-Powered Products
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Innovative solutions for fire safety, project management and compliance
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div data-testid="product-firesight" className="group p-6 rounded-xl bg-white border-2 border-slate-200 hover:shadow-xl transition-all relative" style={{borderColor: '#D4AF37'}}>
              {/* Patent Applied Badge */}
              <div className="absolute -top-3 -right-3 bg-gradient-to-r from-amber-500 to-yellow-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg transform rotate-12">
                Patent Applied
              </div>
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4" style={{backgroundColor: '#003366'}}>
                <FileSearch className="w-7 h-7" style={{color: '#D4AF37'}} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Kavach FireSight Pro</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-3">
                Upload one floor plan + your project scope, get the whole building's BOQ in 2-5 minutes. Compares against architect's BOQ and the Fire NOC, surfaces every gap against NBC 2026 / NFPA / 27 region codes.
              </p>
              <div className="mb-4 space-y-1.5">
                <div className="flex items-center gap-2 text-xs text-slate-700">
                  <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                  <span>ZIP tender bundles + DWG auto-convert</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-700">
                  <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                  <span>Architect-BOQ + Fire NOC gap analyser</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-700">
                  <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                  <span>Smart 1-floor → whole building scaling</span>
                </div>
              </div>
              <Button
                data-testid="try-firesight-btn"
                onClick={() => handleFireSightClick('products_section')}
                style={{backgroundColor: '#003366'}}
                className="hover:opacity-90 text-white w-full text-sm"
              >
                Try FireSight Pro
              </Button>
            </div>
            <div data-testid="product-engineering" className="group p-6 rounded-xl bg-white border-2 border-slate-200 hover:shadow-xl transition-all" style={{borderColor: '#166534'}}>
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4" style={{backgroundColor: '#166534'}}>
                <Calendar className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Kavach Fire Engineering Pro</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-3">
                Advanced project scheduling and management for fire protection contractors with AI-optimized timelines and resource allocation.
              </p>
              <div className="mb-4 space-y-1.5">
                <div className="flex items-center gap-2 text-xs text-slate-700">
                  <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                  <span>Smart Project Scheduling</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-700">
                  <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                  <span>Team & Resource Management</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-700">
                  <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                  <span>Progress Tracking Dashboard</span>
                </div>
              </div>
              <Button
                data-testid="try-engineering-btn"
                onClick={() => handleEngineeringProClick('products_section')}
                style={{backgroundColor: '#166534'}}
                className="hover:opacity-90 text-white w-full text-sm"
              >
                Try Engineering Pro
              </Button>
            </div>
            <div data-testid="product-estimator" className="group p-6 rounded-xl bg-white border border-slate-200 hover:shadow-xl transition-all opacity-75">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4" style={{backgroundColor: '#003366'}}>
                <TrendingUp className="w-7 h-7" style={{color: '#D4AF37'}} />
              </div>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-bold text-slate-900">Kavach Estimator Pro</h3>
                <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-semibold">Soon</span>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-3">
                AI-powered project cost estimation for accurate budgeting and bid preparation.
              </p>
              <Button
                disabled
                variant="outline"
                className="w-full text-sm"
                style={{borderColor: '#003366', color: '#003366'}}
              >
                Notify Me
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Vision, Mission & Core Values Section */}
      <section id="about" className="py-20 px-6 bg-gradient-to-br from-blue-50 via-white to-amber-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{color: '#003366'}}>Our Foundation</h2>
            <p className="text-lg text-slate-600">Guided by excellence, driven by purpose</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            {/* Vision */}
            <Card className="border-2 hover:shadow-2xl transition-all" style={{borderColor: '#D4AF37'}}>
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-full mb-6 flex items-center justify-center mx-auto" style={{backgroundColor: '#003366'}}>
                  <Target className="w-8 h-8" style={{color: '#D4AF37'}} />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-center" style={{color: '#003366'}}>Vision</h3>
                <p className="text-slate-700 leading-relaxed text-center">
                  To make the world safer and smarter by delivering AI-driven security, fire safety, disaster preparedness and advisory solutions that protect people, organizations and communities globally.
                </p>
              </CardContent>
            </Card>

            {/* Mission */}
            <Card className="border-2 hover:shadow-2xl transition-all" style={{borderColor: '#D4AF37'}}>
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-full mb-6 flex items-center justify-center mx-auto" style={{backgroundColor: '#003366'}}>
                  <Lightbulb className="w-8 h-8" style={{color: '#D4AF37'}} />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-center" style={{color: '#003366'}}>Mission</h3>
                <p className="text-slate-700 leading-relaxed text-center">
                  To design, implement and manage technology-driven safety, security and disaster preparedness solutions that provide real-time monitoring, risk assessment, training and advisory services.
                </p>
              </CardContent>
            </Card>

            {/* Core Values */}
            <Card className="border-2 hover:shadow-2xl transition-all" style={{borderColor: '#D4AF37'}}>
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-full mb-6 flex items-center justify-center mx-auto" style={{backgroundColor: '#003366'}}>
                  <Heart className="w-8 h-8" style={{color: '#D4AF37'}} />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-center" style={{color: '#003366'}}>Core Values</h3>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span><strong>Innovation:</strong> AI & robotics for smarter solutions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span><strong>Integrity:</strong> Transparency, ethics & trust</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span><strong>Excellence:</strong> High-quality services & training</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span><strong>Collaboration:</strong> Partnering for everyone&apos;s safety</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span><strong>Sustainability:</strong> Environmental & social responsibility</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Service Verticals */}
      <section id="services" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{color: '#003366'}}>Service Verticals</h2>
            <p className="text-lg text-slate-600">Comprehensive safety and security solutions powered by AI technology</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Technology & AI-Driven Security */}
            <Card className="border-2 hover:shadow-2xl transition-all" style={{borderColor: '#D4AF37'}}>
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-full mb-6 flex items-center justify-center mx-auto" style={{backgroundColor: '#003366'}}>
                  <Shield className="w-8 h-8" style={{color: '#D4AF37'}} />
                </div>
                <h3 className="text-xl font-bold mb-4 text-center" style={{color: '#003366'}}>Technology & AI-Driven Security</h3>
                <ul className="space-y-3 text-slate-700 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span>AI-enhanced CCTV with facial recognition and behavioral analysis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span>Biometric, RFID and smart card access control systems</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span>Intrusion detection and perimeter security monitoring</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span>Real-time IoT sensor networks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span>Robotics and autonomous drone surveillance solutions</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Fire Safety & Disaster Management */}
            <Card className="border-2 hover:shadow-2xl transition-all" style={{borderColor: '#D4AF37'}}>
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-full mb-6 flex items-center justify-center mx-auto" style={{backgroundColor: '#003366'}}>
                  <Zap className="w-8 h-8" style={{color: '#D4AF37'}} />
                </div>
                <h3 className="text-xl font-bold mb-4 text-center" style={{color: '#003366'}}>Fire Safety & Disaster Management</h3>
                <ul className="space-y-3 text-slate-700 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span>Advanced AI-enabled fire detection with early-warning capabilities</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span>Automated fire suppression (Gas & Water)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span>Integrated alarms and notification systems</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span>Comprehensive fire risk audits and compliance consulting</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span>Disaster preparedness and emergency communication solutions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span>Customized safety training, drills and certification programs</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Advisory & Global Safety Services */}
            <Card className="border-2 hover:shadow-2xl transition-all" style={{borderColor: '#D4AF37'}}>
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-full mb-6 flex items-center justify-center mx-auto" style={{backgroundColor: '#003366'}}>
                  <Award className="w-8 h-8" style={{color: '#D4AF37'}} />
                </div>
                <h3 className="text-xl font-bold mb-4 text-center" style={{color: '#003366'}}>Advisory & Global Safety Services</h3>
                <ul className="space-y-3 text-slate-700 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span>Holistic safety and fire audits, risk assessments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span>Regulatory compliance consulting</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span>Safety research and mitigation planning aligned with global standards</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span>Regulatory advisory for domestic and international markets</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span>Global consultancy on safety technology deployment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span>Export and trading of safety, security and fire protection equipment</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* AI Tools for Project Management - NEW 4th Vertical */}
            <Card className="border-2 hover:shadow-2xl transition-all" style={{borderColor: '#D4AF37'}}>
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-full mb-6 flex items-center justify-center mx-auto" style={{backgroundColor: '#003366'}}>
                  <Bot className="w-8 h-8" style={{color: '#D4AF37'}} />
                </div>
                <h3 className="text-xl font-bold mb-4 text-center" style={{color: '#003366'}}>AI Tools for Project Management</h3>
                <ul className="space-y-3 text-slate-700 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span><strong>Kavach FireSight Pro:</strong> AI-powered fire safety compliance analysis with instant NBC/NFPA reports and detailed BOQs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <div className="flex items-center gap-2 flex-wrap">
                      <span><strong>Kavach Estimator Pro:</strong> AI-powered project cost estimation and resource planning tool</span>
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">Coming Soon</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span>Automated documentation generation and compliance tracking</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span>Real-time project insights and predictive analytics</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Fire Insurance - Partnership with Digisecure */}
            <Card className="border-2 hover:shadow-2xl transition-all lg:col-span-2" style={{borderColor: '#D4AF37'}}>
              <CardContent className="p-8">
                <div className="flex flex-col lg:flex-row items-center gap-8">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#003366'}}>
                    <Shield className="w-10 h-10" style={{color: '#D4AF37'}} />
                  </div>
                  <div className="flex-1 text-center lg:text-left">
                    <div className="flex items-center justify-center lg:justify-start gap-3 mb-3">
                      <h3 className="text-xl font-bold" style={{color: '#003366'}}>Fire Insurance Services</h3>
                      <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-semibold">Exclusive Partnership</span>
                    </div>
                    <p className="text-slate-600 mb-4">
                      Protect your assets with comprehensive fire insurance coverage. Through our exclusive partnership with <strong>Digisecure Insurance</strong>, we offer tailored fire insurance solutions that complement our safety systems. Get competitive premiums, quick claim settlements, and complete peace of mind for your residential, commercial, and industrial properties.
                    </p>
                    <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                      <a 
                        href="https://digisecureinsurance.com/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white font-semibold transition-all hover:opacity-90 hover:scale-105"
                        style={{backgroundColor: '#003366'}}
                      >
                        <Shield className="w-4 h-4" />
                        Get Fire Insurance Quote
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <span>Powered by</span>
                        <span className="font-semibold" style={{color: '#003366'}}>Digisecure Insurance</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{color: '#003366'}}>Industries Served</h2>
            <p className="text-lg text-slate-600">Comprehensive fire safety and security solutions across diverse sectors</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Construction & Infrastructure */}
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{backgroundColor: '#003366'}}>
                  <Building2 className="w-8 h-8" style={{color: '#D4AF37'}} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Construction & Infrastructure</h3>
                <ul className="text-sm text-slate-600 space-y-2">
                  <li>• Building Construction</li>
                  <li>• Roads & Highways</li>
                  <li>• Bridges & Tunnels</li>
                  <li>• Metro & Railway Projects</li>
                </ul>
              </CardContent>
            </Card>

            {/* Industrial & Manufacturing */}
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{backgroundColor: '#003366'}}>
                  <Factory className="w-8 h-8" style={{color: '#D4AF37'}} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Industrial & Manufacturing</h3>
                <ul className="text-sm text-slate-600 space-y-2">
                  <li>• Manufacturing Plants</li>
                  <li>• Oil & Gas Refineries</li>
                  <li>• Warehouses & Logistics</li>
                  <li>• Industrial Facilities</li>
                </ul>
              </CardContent>
            </Card>

            {/* Commercial & Hospitality */}
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{backgroundColor: '#003366'}}>
                  <Building2 className="w-8 h-8" style={{color: '#D4AF37'}} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Commercial & Hospitality</h3>
                <ul className="text-sm text-slate-600 space-y-2">
                  <li>• Office Buildings</li>
                  <li>• Shopping Malls</li>
                  <li>• Hotels & Resorts</li>
                  <li>• Residential Complexes</li>
                </ul>
              </CardContent>
            </Card>

            {/* Healthcare & Education */}
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{backgroundColor: '#003366'}}>
                  <Heart className="w-8 h-8" style={{color: '#D4AF37'}} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Healthcare & Education</h3>
                <ul className="text-sm text-slate-600 space-y-2">
                  <li>• Hospitals & Clinics</li>
                  <li>• Medical Centers</li>
                  <li>• Schools & Colleges</li>
                  <li>• Universities & Institutions</li>
                </ul>
              </CardContent>
            </Card>

            {/* Critical Infrastructure */}
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{backgroundColor: '#003366'}}>
                  <Route className="w-8 h-8" style={{color: '#D4AF37'}} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Critical Infrastructure</h3>
                <ul className="text-sm text-slate-600 space-y-2">
                  <li>• Data Centers</li>
                  <li>• Airports & Ports</li>
                  <li>• Transport Terminals</li>
                  <li>• Power Plants</li>
                </ul>
              </CardContent>
            </Card>

            {/* Government & Public */}
            <Card className="border-2 hover:shadow-xl transition-all">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{backgroundColor: '#003366'}}>
                  <Shield className="w-8 h-8" style={{color: '#D4AF37'}} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Government & Public Sector</h3>
                <ul className="text-sm text-slate-600 space-y-2">
                  <li>• Government Buildings</li>
                  <li>• Public Facilities</li>
                  <li>• Defense Installations</li>
                  <li>• Municipal Infrastructure</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Quality Assurance & Commitment to Safety */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{color: '#003366'}}>Quality Assurance & Commitment to Safety</h2>
            <p className="text-lg text-slate-600">Excellence in every solution we deliver</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Quality Assurance */}
            <Card className="border-2 hover:shadow-2xl transition-all h-full" style={{borderColor: '#D4AF37'}}>
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-full mb-6 flex items-center justify-center mx-auto" style={{backgroundColor: '#003366'}}>
                  <Award className="w-8 h-8" style={{color: '#D4AF37'}} />
                </div>
                <h3 className="text-2xl font-bold mb-6 text-center" style={{color: '#003366'}}>Quality Assurance</h3>
                <ul className="space-y-4 text-slate-700">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span><strong>Stringent Quality Control:</strong> Adherence to rigorous quality control measures throughout design, installation and maintenance processes</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span><strong>Industry Standards Compliance:</strong> Full compliance with NBC, NFPA, BIS and international safety standards</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span><strong>Premium Materials:</strong> Use of high-quality, certified equipment and materials for all installations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span><strong>Regular Audits:</strong> Continuous quality audits and assessments to ensure optimal system performance</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span><strong>Customer Satisfaction:</strong> Unwavering commitment to meeting and exceeding client expectations</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Commitment to Safety */}
            <Card className="border-2 hover:shadow-2xl transition-all h-full" style={{borderColor: '#D4AF37'}}>
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-full mb-6 flex items-center justify-center mx-auto" style={{backgroundColor: '#003366'}}>
                  <Shield className="w-8 h-8" style={{color: '#D4AF37'}} />
                </div>
                <h3 className="text-2xl font-bold mb-6 text-center" style={{color: '#003366'}}>Commitment to Safety</h3>
                <ul className="space-y-4 text-slate-700">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span><strong>People First:</strong> Prioritizing the safety and well-being of people above all else</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span><strong>Proactive Risk Management:</strong> Advanced risk assessment and mitigation strategies to prevent incidents</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span><strong>Real-time Monitoring:</strong> Continuous system monitoring and emergency response capabilities</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span><strong>Comprehensive Training:</strong> Regular safety drills, training programs and certification courses for all stakeholders</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <span><strong>Continuous Innovation:</strong> Leveraging AI and cutting-edge technology to enhance safety outcomes</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Compliance Standards - Both Products */}
      <section className="py-20 px-6" style={{backgroundColor: '#003366'}}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Our AI-Powered Solutions</h2>
            <p className="text-lg text-slate-300 max-w-3xl mx-auto">
              Kavach delivers comprehensive fire safety and project management solutions through our integrated product suite
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {/* FireSight Pro */}
            <div className="bg-white/10 backdrop-blur rounded-2xl p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{backgroundColor: '#D4AF37'}}>
                  <FileSearch className="w-6 h-6" style={{color: '#003366'}} />
                </div>
                <h3 className="text-2xl font-bold text-white">Kavach FireSight Pro</h3>
              </div>
              <p className="text-slate-200 mb-6 leading-relaxed">
                AI-powered fire safety compliance analysis ensuring your systems meet NBC, NFPA and BIS standards. Upload drawings and receive instant compliance reports with detailed equipment BOQ and gap analysis.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="flex items-center gap-2 p-3 bg-white/10 rounded-lg">
                  <CheckCircle className="w-5 h-5" style={{color: '#D4AF37'}} />
                  <span className="text-sm text-white">Detection Systems</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-white/10 rounded-lg">
                  <CheckCircle className="w-5 h-5" style={{color: '#D4AF37'}} />
                  <span className="text-sm text-white">Fighting Equipment</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-white/10 rounded-lg">
                  <CheckCircle className="w-5 h-5" style={{color: '#D4AF37'}} />
                  <span className="text-sm text-white">Suppression Systems</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-white/10 rounded-lg">
                  <CheckCircle className="w-5 h-5" style={{color: '#D4AF37'}} />
                  <span className="text-sm text-white">BIS Standards</span>
                </div>
              </div>
              <Button
                data-testid="compliance-firesight-btn"
                onClick={() => handleFireSightClick('compliance_section')}
                size="lg"
                style={{backgroundColor: '#D4AF37'}}
                className="hover:opacity-90 w-full py-6 text-lg"
              >
                <span style={{color: '#003366'}} className="font-semibold">Start Compliance Analysis</span>
              </Button>
            </div>

            {/* Fire Engineering Pro */}
            <div className="bg-white/10 backdrop-blur rounded-2xl p-8 border border-white/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{backgroundColor: '#166534'}}>
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white">Kavach Fire Engineering Pro</h3>
              </div>
              <p className="text-slate-200 mb-6 leading-relaxed">
                Advanced project scheduling and management platform for fire protection contractors. AI-optimized timelines, resource allocation and progress tracking for seamless project delivery.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="flex items-center gap-2 p-3 bg-white/10 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-sm text-white">Smart Scheduling</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-white/10 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-sm text-white">Team Management</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-white/10 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-sm text-white">Progress Tracking</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-white/10 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-sm text-white">Documentation</span>
                </div>
              </div>
              <Button
                data-testid="compliance-engineering-btn"
                onClick={() => handleEngineeringProClick('compliance_section')}
                size="lg"
                style={{backgroundColor: '#166534'}}
                className="hover:opacity-90 w-full py-6 text-lg text-white"
              >
                Start Project Management
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer with Company Details */}
      {/* Leadership & Advisory */}
      {SHOW_TEAM_SECTION && <TeamSection />}

      <footer id="contact" className="bg-slate-900 text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            {/* Company Info */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img src={CONTENT_LOGO_URL} alt="Kavach Logo" className="h-14 w-14 object-contain" />
                <div>
                  <h3 className="text-xl font-bold">Kavach Infra Solutions LLP</h3>
                  <p className="text-sm" style={{color: '#D4AF37'}}>Safety & Security Experts</p>
                </div>
              </div>
              <div className="mb-4 p-4 rounded-lg" style={{backgroundColor: 'rgba(212, 175, 55, 0.1)'}}>
                <p className="text-xl font-bold mb-1" style={{color: '#D4AF37'}}>रक्षा | सुरक्षा | सजगता</p>
                <p className="text-sm text-slate-300">Protection | Safety | Awareness</p>
              </div>
              <p className="text-slate-400 leading-relaxed">
                Premier provider of cutting-edge electronic security, fire safety, disaster management and AI-driven project management solutions committed to protecting people, properties and assets.
              </p>
            </div>

            {/* Contact Details */}
            <div>
              <h4 className="text-lg font-bold mb-6" style={{color: '#D4AF37'}}>Contact Us</h4>
              <div className="space-y-4">
                {/* Corporate Office - Pune */}
                <div>
                  <p className="text-xs font-bold mb-2" style={{color: '#D4AF37'}}>Corporate Office</p>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 mt-1 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <div>
                      <p className="text-slate-300 text-sm leading-relaxed">
                        1304, 13th Floor, A Wing, Dev Corpora, Eastern Express Highway, Opp. Cadbury Junction, Thane West, Maharashtra 400601
                      </p>
                    </div>
                  </div>
                </div>

                {/* Registered Office - Goa */}
                <div>
                  <p className="text-xs font-bold mb-2" style={{color: '#D4AF37'}}>Registered Office</p>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 mt-1 flex-shrink-0" style={{color: '#D4AF37'}} />
                    <div>
                      <p className="text-slate-300 text-sm leading-relaxed">
                        Villa 2 Skyline, Vistas HN: SYNO -42/1 Plot No 72, Socorro Porvorim Bardez, Tiswadi, North Goa, Goa 403521
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 flex-shrink-0" style={{color: '#D4AF37'}} />
                  <div>
                    <p className="text-slate-300 text-sm">+91 9890838368</p>
                    <p className="text-slate-300 text-sm">+91 9593843999</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 flex-shrink-0" style={{color: '#D4AF37'}} />
                  <a href="mailto:info@kavachinfra.in" className="text-slate-300 text-sm hover:text-amber-300 transition-colors">
                    info@kavachinfra.in
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 flex-shrink-0" style={{color: '#D4AF37'}} />
                  <a href="https://www.kavachinfra.in" target="_blank" rel="noopener noreferrer" className="text-slate-300 text-sm hover:text-amber-300 transition-colors">
                    www.kavachinfra.in
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-bold mb-6" style={{color: '#D4AF37'}}>Our Products</h4>
              <div className="space-y-3 mb-6">
                <button onClick={() => handleFireSightClick('footer')} className="block text-slate-300 text-sm hover:text-amber-300 transition-colors text-left">
                  Kavach FireSight Pro
                </button>
                <button onClick={() => handleEngineeringProClick('footer')} className="block text-slate-300 text-sm hover:text-amber-300 transition-colors text-left">
                  Kavach Fire Engineering Pro
                </button>
                <span className="block text-slate-500 text-sm">
                  Kavach Estimator Pro (Coming Soon)
                </span>
              </div>
              
              <h4 className="text-lg font-bold mb-4 mt-8" style={{color: '#D4AF37'}}>Streamlining Safety Projects</h4>
              <p className="text-slate-400 text-sm leading-relaxed mb-4">
                At Kavach, we leverage our AI-powered product suite to deliver end-to-end fire safety solutions. From compliance analysis with FireSight Pro to project scheduling with Engineering Pro, our integrated approach ensures faster project delivery, reduced costs and complete regulatory compliance.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" style={{color: '#D4AF37'}} />
                  <span className="text-slate-300 text-sm">Unified Project Management</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" style={{color: '#D4AF37'}} />
                  <span className="text-slate-300 text-sm">AI-Driven Efficiency</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" style={{color: '#D4AF37'}} />
                  <span className="text-slate-300 text-sm">Complete Compliance Coverage</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © 2026 Kavach Infra Solutions LLP. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <a href="/privacy-policy" className="text-slate-400 hover:text-amber-300 text-sm transition-colors">Privacy Policy</a>
              <a href="/terms-and-conditions" className="text-slate-400 hover:text-amber-300 text-sm transition-colors">Terms of Service</a>
              <button onClick={() => scrollToSection('contact')} className="text-slate-400 hover:text-amber-300 text-sm transition-colors">Contact</button>
            </div>
          </div>
        </div>
      </footer>

      {/* Admin Login Modal */}
    </div>
  );
}

const TEAM = [
  {
    name: "Navya Singh", role: "Founder & Director",
    group: "Board of Directors",
    education: "Economics · Media Strategy · Sustainability",
    bio: "Founder of Fact Forth LLP and the digital news platform 'News with Navya'. Drives corporate strategy, branding & investor relations at Kavach.",
  },
  {
    name: "Col (Retd) Pankaj Narayan", role: "Co-Founder & Director",
    group: "Board of Directors",
    education: "B.Tech — Information Technology & Communication",
    bio: "35 years of distinguished service in the Indian Army. Leads technology deployment, discipline-driven operations & institutional liaison.",
  },
  {
    name: "Ashok Menon", role: "Advisor — Fire & Life Safety",
    group: "Advisory Board",
    bio: "Ex-Director, Directorate of Fire & Emergency Services, Govt. of Goa (2006–2021). 35+ years in fire prevention, protection & audits. Advisor to FSAI, Goa Chapter.",
  },
  {
    name: "Ravi Nandan Sinha", role: "Advisor — MSME & Business Growth",
    group: "Advisory Board",
    bio: "Director — Development & Board Member, MSME Business Forum of India. 26+ years of cross-sector leadership (TATA AIA, Kotak Life). Champions global market access for Indian MSMEs.",
  },
];

function TeamSection() {
  const groups = ["Board of Directors", "Advisory Board"];
  return (
    <section id="team" data-testid="team-section" className="py-20 px-6 bg-slate-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold" style={{ color: '#003366' }}>Leadership & Advisory</h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
            Decades of fire-service command, defence-grade discipline and business leadership behind every Kavach deliverable.
          </p>
        </div>
        {groups.map((g) => (
          <div key={g} className="mb-12">
            <h3 className="text-sm font-bold uppercase tracking-widest text-center mb-8" style={{ color: '#D4AF37' }}>{g}</h3>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {TEAM.filter((m) => m.group === g).map((m) => (
                <Card key={m.name} data-testid={`team-card-${m.name.toLowerCase().replace(/[^a-z]+/g, '-')}`} className="border-slate-200 shadow-md hover:shadow-xl transition-shadow bg-white border-t-4" style={{ borderTopColor: '#003366' }}>
                  <CardContent className="p-8">
                    <h4 className="text-xl font-bold" style={{ color: '#003366' }}>{m.name}</h4>
                    <p className="text-sm font-semibold mt-1" style={{ color: '#D4AF37' }}>{m.role}</p>
                    {m.education && (
                      <div className="inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-full text-xs font-bold" style={{ backgroundColor: '#003366', color: '#D4AF37' }}>
                        <GraduationCap className="w-4 h-4" />
                        {m.education}
                      </div>
                    )}
                    <p className="text-sm text-slate-600 mt-4 leading-relaxed">{m.bio}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}